import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  ChevronDownIcon, 
  ChevronRightIcon,
  QuestionMarkCircleIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';

const FAQPage = () => {
  const [expandedItems, setExpandedItems] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const toggleItem = (itemId) => {
    setExpandedItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  const faqData = [
    {
      id: 1,
      category: 'registration',
      question: 'How do I register my child for athletics?',
      answer: 'Registration is completed online through our athletics portal. Visit the registration page, select your sport, complete the required forms, and submit payment. All forms must be submitted before the first practice.'
    },
    {
      id: 2,
      category: 'registration',
      question: 'What forms are required for athletic participation?',
      answer: 'Required forms include: Athletic Physical (within 12 months), Emergency Medical Information, Code of Conduct Agreement, Transportation Permission, and Concussion Awareness Form. All forms must be completed and submitted before participation.'
    },
    {
      id: 3,
      category: 'registration',
      question: 'When does registration open for each season?',
      answer: 'Fall sports registration opens July 15th, Winter sports registration opens October 15th, and Spring sports registration opens January 15th. Early registration is encouraged as some sports have limited roster spots.'
    },
    {
      id: 4,
      category: 'fees',
      question: 'How much does it cost to participate in athletics?',
      answer: 'Athletic fees vary by sport, ranging from $130 for track & field to $325 for football. Fees include participation fee, equipment rental, and uniform deposit. Payment plans are available for fees over $200.'
    },
    {
      id: 5,
      category: 'fees',
      question: 'Are there any additional costs beyond the athletic fee?',
      answer: 'Students may need to purchase personal equipment like cleats, gloves, or protective gear depending on the sport. Team meals, travel expenses, and optional team apparel may also incur additional costs.'
    },
    {
      id: 6,
      category: 'fees',
      question: 'Is financial assistance available?',
      answer: 'Yes, financial assistance is available for families who qualify. Contact the Athletic Director confidentially to discuss options. We believe financial constraints should not prevent student participation.'
    },
    {
      id: 7,
      category: 'eligibility',
      question: 'What are the academic requirements for athletic participation?',
      answer: 'Student-athletes must maintain a minimum 2.0 GPA and be enrolled in at least 5 classes. Students with 2 or more failing grades are immediately ineligible. Academic progress is monitored weekly.'
    },
    {
      id: 8,
      category: 'eligibility',
      question: 'What happens if my child becomes academically ineligible?',
      answer: 'Academically ineligible students are immediately suspended from all athletic activities including practices and games. Eligibility can be restored once academic requirements are met and verified by the guidance office.'
    },
    {
      id: 9,
      category: 'eligibility',
      question: 'Can my child participate in multiple sports?',
      answer: 'Yes, multi-sport participation is encouraged. However, students cannot participate in two sports simultaneously during the same season. Priority is given to the sport in which the student is currently participating.'
    },
    {
      id: 10,
      category: 'practice',
      question: 'What is the attendance policy for practices?',
      answer: 'Regular attendance at all practices is mandatory. Unexcused absences may result in reduced playing time or suspension. Students must communicate absences to coaches in advance whenever possible.'
    },
    {
      id: 11,
      category: 'practice',
      question: 'What should my child bring to practice?',
      answer: 'Students should bring appropriate athletic clothing, water bottle, and any personal equipment required for their sport. The school provides team equipment, but personal items like cleats and gloves must be purchased separately.'
    },
    {
      id: 12,
      category: 'practice',
      question: 'How long are practices and when do they occur?',
      answer: 'Practices typically last 1.5-2 hours and are held after school Monday through Friday. Some sports may have Saturday practices or conditioning sessions. Specific schedules are provided by individual coaches.'
    },
    {
      id: 13,
      category: 'games',
      question: 'How is playing time determined?',
      answer: 'Playing time decisions are made by coaches based on factors including skill level, effort in practice, attitude, academic standing, and team needs. Coaches are not required to provide equal playing time.'
    },
    {
      id: 14,
      category: 'games',
      question: 'Can parents discuss playing time with coaches?',
      answer: 'Playing time is not an appropriate topic for parent-coach discussions. Parents should focus on their child\'s development, team rules, and ways to help their child improve. Follow the 24-hour rule before addressing any concerns.'
    },
    {
      id: 15,
      category: 'games',
      question: 'What is the transportation policy for away games?',
      answer: 'Students must travel to and from away games on school-provided transportation. Parents may request to transport their own child home with written permission. Students may only ride with parents or designated guardians.'
    },
    {
      id: 16,
      category: 'safety',
      question: 'What happens if my child gets injured during athletics?',
      answer: 'All injuries must be reported to coaches immediately. Our athletic trainer will assess the injury and determine appropriate treatment. Medical clearance from a physician is required before returning to play after any injury.'
    },
    {
      id: 17,
      category: 'safety',
      question: 'What is the concussion protocol?',
      answer: 'Any suspected concussion results in immediate removal from activity. Students must be cleared by a medical professional trained in concussion management before returning to play. We follow state guidelines for concussion protocols.'
    },
    {
      id: 18,
      category: 'safety',
      question: 'Is athletic insurance required?',
      answer: 'Students must have insurance coverage for athletic participation. Families can provide proof of personal insurance or purchase school-sponsored athletic insurance. Coverage information must be on file before participation.'
    },
    {
      id: 19,
      category: 'communication',
      question: 'How do I communicate with my child\'s coach?',
      answer: 'Contact coaches directly via email or phone for appropriate concerns. Follow the 24-hour rule - wait 24 hours after games before discussing concerns. If issues cannot be resolved, contact the Athletic Director.'
    },
    {
      id: 20,
      category: 'communication',
      question: 'How will I receive updates about games and events?',
      answer: 'Updates are sent via email, text alerts, and posted on our website. Make sure your contact information is current in the registration system. Follow our social media accounts for additional updates and photos.'
    }
  ];

  const categories = [
    { value: 'all', label: 'All Categories' },
    { value: 'registration', label: 'Registration' },
    { value: 'fees', label: 'Fees & Payment' },
    { value: 'eligibility', label: 'Eligibility' },
    { value: 'practice', label: 'Practices' },
    { value: 'games', label: 'Games & Competition' },
    { value: 'safety', label: 'Safety & Injuries' },
    { value: 'communication', label: 'Communication' }
  ];

  const filteredFAQs = faqData.filter(item => {
    const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
    const matchesSearch = searchTerm === '' || 
      item.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.answer.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-primary text-white py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="mb-4">
            <Link to="/family-resources" className="text-blue-200 hover:text-white">
              Family Resources
            </Link>
            <span className="mx-2 text-blue-200">/</span>
            <span className="text-white">FAQ</span>
          </nav>
          <h1 className="text-4xl font-bold mb-4">
            Frequently Asked Questions
          </h1>
          <p className="text-xl text-blue-100">
            Find answers to common questions about East End Prep athletics.
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Search and Filter */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search FAQs
              </label>
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search questions and answers..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
            </div>
            
            {/* Category Filter */}
            <div className="md:w-64">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Category
              </label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                {categories.map(category => (
                  <option key={category.value} value={category.value}>
                    {category.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* FAQ Items */}
        <div className="space-y-4">
          {filteredFAQs.length === 0 ? (
            <div className="bg-white rounded-lg shadow-md p-8 text-center">
              <QuestionMarkCircleIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                No FAQs Found
              </h3>
              <p className="text-gray-600">
                Try adjusting your search terms or category filter.
              </p>
            </div>
          ) : (
            filteredFAQs.map((item) => (
              <div key={item.id} className="bg-white rounded-lg shadow-md overflow-hidden">
                <button
                  onClick={() => toggleItem(item.id)}
                  className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors duration-200"
                >
                  <div className="flex items-start">
                    <QuestionMarkCircleIcon className="w-5 h-5 text-primary mr-3 mt-0.5 flex-shrink-0" />
                    <h3 className="text-lg font-semibold text-gray-900 pr-4">
                      {item.question}
                    </h3>
                  </div>
                  {expandedItems[item.id] ? (
                    <ChevronDownIcon className="w-5 h-5 text-gray-500 flex-shrink-0" />
                  ) : (
                    <ChevronRightIcon className="w-5 h-5 text-gray-500 flex-shrink-0" />
                  )}
                </button>
                
                {expandedItems[item.id] && (
                  <div className="px-6 pb-6">
                    <div className="border-t border-gray-200 pt-4 ml-8">
                      <p className="text-gray-600 leading-relaxed">
                        {item.answer}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        {/* Contact Section */}
        <div className="bg-white rounded-lg shadow-md p-8 mt-8">
          <h3 className="text-xl font-semibold text-gray-900 mb-4">
            Still Have Questions?
          </h3>
          <p className="text-gray-600 mb-6">
            If you can't find the answer to your question, please don't hesitate to contact 
            our Athletic Department. We're here to help!
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link
              to="/contact"
              className="btn-primary inline-flex items-center justify-center"
            >
              Contact Athletic Department
            </Link>
            <a
              href="mailto:athletics@eastendprep.edu?subject=Question about Athletics"
              className="btn-secondary inline-flex items-center justify-center"
            >
              Email Your Question
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FAQPage;